import { Box, Button, IconButton, Typography } from "@mui/material";
import { Trash2 } from "lucide-react";
import { useRevealICSUrl, useUpdateICSUrl } from "services/calendar";
import { ICSExportIntegration } from "protogen/calendar_service_pb";
import CopyButton from "components/common/CopyButton";
import { calendarSyncStatus } from "./CalendarSyncStatus";
import useIsMobile from "components/hooks/useIsMobile";
import FlexBox from "components/common/FlexBox";

export default ({
  link,
  refresh,
}: {
  link: ICSExportIntegration;
  refresh: () => void;
}) => {
  const { request: updateLink, loading: updateLoading } = useUpdateICSUrl();

  const onDisableLink = async () => {
    await updateLink({
      ref: link.ref,
      updatedName: "",
      updatedStatus: "deactivated",
    });
    refresh();
  };

  const { request: revealRequest } = useRevealICSUrl();
  const [icon, byline] = calendarSyncStatus(
    link.status as "active" | "error" | "deactivated",
    Number(link.lastAccessedSec),
  );
  const isMobile = useIsMobile();

  return (
    <Box
      key={link.ref}
      sx={{
        display: "flex",
        alignItems: isMobile ? "start" : "center",
        justifyContent: "space-between",
        gap: isMobile ? "2px" : "16px",
        padding: isMobile ? "16px 20px 12px" : "12px 24px",
        border: "1px solid #E2E3E4",
        borderRadius: "12px",
        flexDirection: isMobile ? "column" : "row",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          width: "100%",
        }}
      >
        <FlexBox
          stackedMobile
          sx={{ justifyContent: "space-between", gap: "2px" }}
        >
          <Box display="flex" flexDirection="column" gap="2px">
            <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {icon}
              <Typography
                variant="bodyHeavy"
                sx={{ display: "flex", gap: "4px" }}
              >
                {link.name || "Exported Calendar"}
              </Typography>
            </Box>

            <Typography variant="bodySmall" color="textSecondary">
              {byline}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: "12px",
              width: isMobile ? "100%" : "auto",
              justifyContent: "space-between",
            }}
          >
            <CopyButton
              variant="text"
              label="Copy link"
              showIcon={false}
              onReveal={async () => {
                const resp = await revealRequest({ ref: link.ref });
                return resp?.export?.icsUrlPath || "";
              }}
            />
            {isMobile ? (
              <Button
                variant="text"
                size="small"
                color="error"
                onClick={() => onDisableLink()}
                disabled={updateLoading}
              >
                Delete
              </Button>
            ) : (
              <IconButton
                onClick={() => onDisableLink()}
                disabled={updateLoading}
                edge="end"
                sx={{ marginRight: "4px" }}
              >
                <Trash2 width="20" stroke="#8E9598" />
              </IconButton>
            )}
          </Box>
        </FlexBox>
      </Box>
    </Box>
  );
};
